import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { KeyRound, Lock, PlugZap, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Input } from "@/components/atoms/studio/ui/input";
import { Skeleton } from "@/components/atoms/studio/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  useMcpConnectorPresets,
  useMcpTools,
  useMcpConnectors,
  useCreateMcpConnector,
  useStartMcpOAuth,
  useDeleteMcpConnector,
  useDiscoverMcpConnector,
  useMcpConnectorAllowedTools,
  useUpdateMcpConnectorSettings,
} from "@/hooks/tools";
import type { McpConnector, McpConnectorPreset, StartMcpOAuthInput } from "@/services/mcp-gateway";
import { isConnectorUsable } from "@/services/mcp-gateway";
import { buildProviderCards } from "./toolsPageModel";
import type { ProviderCard } from "./toolsPageModel";
import { ToolGroups } from "./ToolGroups";

const FALLBACK_CONNECTOR_PRESETS: McpConnectorPreset[] = [
  {
    key: "jira",
    title: "Jira",
    description: "Jira MCP for issues, projects, releases, and sprint context.",
    base_url_placeholder: "https://mcp.atlassian.com/v1/mcp",
    credential_label: "Jira OAuth token",
    auth_type: "oauth",
  },
  {
    key: "github",
    title: "GitHub",
    description: "GitHub MCP for repos, branches, pull requests, reviews, and issues.",
    base_url_placeholder: "https://api.githubcopilot.com/mcp/",
    credential_label: "GitHub OAuth token",
    auth_type: "oauth",
  },
  {
    key: "notion",
    title: "Notion",
    description: "Notion MCP for pages, databases, specs, and run artifacts.",
    base_url_placeholder: "https://mcp.notion.com/mcp",
    credential_label: "Notion OAuth token",
    auth_type: "oauth",
  },
];

function humanMcpError(error?: string | null): string | null {
  if (!error) return null;
  if (error.includes("jira_oauth_expired_reconnect_required"))
    return "Jira login expired. Reconnect Jira to refresh the MCP token.";
  if (error.includes("notion_oauth_expired_reconnect_required"))
    return "Notion login expired. Reconnect Notion to refresh the MCP token.";
  if (error.includes("linear_oauth_expired_reconnect_required"))
    return "Linear login expired. Reconnect Linear to refresh the MCP token.";
  if (error.includes("github_oauth_expired_reconnect_required"))
    return "GitHub login expired. Reconnect GitHub to refresh the MCP token.";
  if (error.endsWith("_oauth_expired_reconnect_required"))
    return "This connection expired. Reconnect it to keep using its tools.";
  if (error.includes("mcp_server_requires_dynamic_client_registration"))
    return "This server doesn't support automatic sign-in. Ask your admin to add it with an API token.";
  if (error.includes("mcp_server_not_found"))
    return "Your admin removed this server.";
  if (error.includes("mcp_oauth_upstream_error"))
    return "Couldn't reach this server's sign-in. Try again later.";
  if (error.includes("mcp_oauth_endpoints_missing"))
    return "This server's sign-in setup is invalid. Ask your admin to check it.";
  if (error.includes("credential_secret_required"))
    return "Enter a token to connect.";
  if (error.includes("state_not_found"))
    return "The login session expired or was already used. Start the login again from this page.";
  if (error.includes("missing_code_or_state"))
    return "The login callback was missing required OAuth data. Start the login again.";
  if (error.includes("monday_oauth_not_configured"))
    return "Monday OAuth app credentials are missing in the MCP gateway.";
  if (error.includes("jira_oauth_not_configured"))
    return "Jira OAuth app credentials are missing in the MCP gateway.";
  if (error.includes("github_oauth_not_configured"))
    return "GitHub OAuth app credentials are missing in the MCP gateway.";
  if (error.includes("linear_oauth_not_configured"))
    return "Linear OAuth is not available from the MCP gateway. Refresh the page and try again.";
  return error;
}

function mutationErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown; message?: unknown } } })
    ?.response?.data;
  const backendMessage = detail?.detail || detail?.message;
  if (typeof backendMessage === "string" && backendMessage.trim())
    return humanMcpError(backendMessage) || backendMessage;
  if (error instanceof Error && error.message.trim())
    return humanMcpError(error.message) || error.message;
  return fallback;
}

type OAuthNotice = { tone: "success" | "error"; message: string; provider?: string };

function oauthNoticeFromUrl(): OAuthNotice | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const connected = params.get("mcp_connected");
  if (connected) return { tone: "success", message: `${connected} MCP connected.`, provider: connected };
  const error = params.get("mcp_oauth_detail") || params.get("mcp_oauth_error");
  const message = humanMcpError(error);
  return message ? { tone: "error", message } : null;
}

function clearMcpOAuthUrlParams() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const keys = ["mcp_connected", "connector_id", "mcp_oauth_error", "mcp_oauth_detail"];
  let changed = false;
  for (const key of keys) {
    if (url.searchParams.has(key)) { url.searchParams.delete(key); changed = true; }
  }
  if (changed) window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

function formatRefreshed(iso?: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** Tools the gateway blocks on a read-only connection, once its allowed list has loaded. */
function blockedCount(connector: McpConnector, allowedByConnector: Map<string, Set<string>>): number | null {
  const allowed = allowedByConnector.get(connector.id);
  return allowed ? Math.max(0, connector.tool_count - allowed.size) : null;
}

export function ToolsLayout() {
  const mcpToolsQ = useMcpTools();
  const presetsQ = useMcpConnectorPresets();
  const connectorsQ = useMcpConnectors();
  const createConnectorM = useCreateMcpConnector();
  const startOAuthM = useStartMcpOAuth();
  const deleteConnectorM = useDeleteMcpConnector();
  const discoverConnectorM = useDiscoverMcpConnector();
  const settingsM = useUpdateMcpConnectorSettings();

  const connectorPresets = presetsQ.data?.length ? presetsQ.data : FALLBACK_CONNECTOR_PRESETS;
  const connectors = useMemo(() => connectorsQ.data ?? [], [connectorsQ.data]);
  const liveTools = mcpToolsQ.data ?? [];
  const allowedByConnector = useMcpConnectorAllowedTools(connectors);
  const cards = useMemo(() => buildProviderCards(connectorPresets, connectors), [connectorPresets, connectors]);

  const [oauthNotice, setOauthNotice] = useState<OAuthNotice | null>(() => oauthNoticeFromUrl());
  // Coming back from a provider's login, open the card that was just connected.
  const [selectedKey, setSelectedKey] = useState<string | null>(() => oauthNoticeFromUrl()?.provider ?? null);
  const selected: ProviderCard = cards.find((c) => c.key === selectedKey) ?? cards[0];
  const [addingAnother, setAddingAnother] = useState(false);

  const formPreset = selected.preset ?? connectorPresets[0];
  const authType = formPreset.auth_type || "oauth";
  // Org-catalog servers (added by an org admin) carry their URL; members only
  // supply a credential. The gateway ignores any URL we send for them anyway.
  const isCatalogPreset = formPreset.key.startsWith("custom-");
  const [connectorName, setConnectorName] = useState(formPreset.title);
  const [connectorUrl, setConnectorUrl] = useState("");
  const [credentialLabel, setCredentialLabel] = useState(formPreset.credential_label);
  const [credentialSecret, setCredentialSecret] = useState("");

  useEffect(() => {
    const notice = oauthNoticeFromUrl();
    if (!notice) return;
    setOauthNotice(notice);
    clearMcpOAuthUrlParams();
    void connectorsQ.refetch();
    void mcpToolsQ.refetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectCard = (card: ProviderCard) => {
    setSelectedKey(card.key);
    setAddingAnother(false);
    const p = card.preset;
    setConnectorName(p?.title ?? card.title);
    setConnectorUrl("");
    setCredentialLabel(p?.credential_label ?? "");
    setCredentialSecret("");
  };

  const saveConnector = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = connectorName.trim();
    const baseUrl = isCatalogPreset ? formPreset.base_url_placeholder : connectorUrl.trim();
    if (authType === "oauth") {
      if (startOAuthM.isPending) return;
      startOAuthM.mutate({ provider: formPreset.key as StartMcpOAuthInput["provider"], name: name || formPreset.title });
      return;
    }
    if (!name || !baseUrl || createConnectorM.isPending) return;
    createConnectorM.mutate(
      { provider: formPreset.key, name, base_url: baseUrl, credential_label: credentialLabel.trim() || formPreset.credential_label, credential_secret: credentialSecret },
      { onSuccess: () => { setCredentialSecret(""); setConnectorUrl(""); setAddingAnother(false); } },
    );
  };

  const connectedCount = cards.filter((c) => c.state === "connected").length;
  const reconnectCount = cards.filter((c) => c.state === "needs_reconnect").length;
  const readOnlyCount = connectors.filter((c) => c.read_only).length;
  const showConnectForm = selected.state === "not_connected" || addingAnother;

  return (
    <div className="space-y-6">
      <div className="studio-card p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Connections</h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Connect the services your agents can use. Agents only see tools from services you've connected.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full border border-border px-3 py-1 text-muted-foreground">{connectedCount} connected</span>
            <span className="rounded-full border border-border px-3 py-1 text-muted-foreground">{liveTools.length} tools</span>
            {readOnlyCount ? <span className="rounded-full border border-warning/40 px-3 py-1 text-warning">{readOnlyCount} read-only</span> : null}
            {reconnectCount ? <span className="rounded-full border border-destructive/40 px-3 py-1 text-destructive">{reconnectCount} need{reconnectCount === 1 ? "s" : ""} reconnect</span> : null}
          </div>
        </div>

        {oauthNotice ? (
          <div className={cn("mt-4 rounded-md border p-3 text-sm", oauthNotice.tone === "success" ? "border-success/30 bg-success/5 text-success" : "border-destructive/30 bg-destructive/5 text-destructive")}>
            {/* Catalog keys are opaque (custom-<hex>); show the server's name. Resolved at
                render because presets load after the notice is read from the URL. */}
            {oauthNotice.provider?.startsWith("custom-")
              ? `${connectorPresets.find((p) => p.key === oauthNotice.provider)?.title ?? "MCP server"} MCP connected.`
              : oauthNotice.message}
          </div>
        ) : null}

        {connectorsQ.isError ? (
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Could not load your connections from the MCP gateway.
          </div>
        ) : null}

        <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.75fr)]">
          <div className="grid content-start gap-3 md:grid-cols-2">
            {connectorsQ.isLoading
              ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-36 rounded-lg" />)
              : cards.map((card) => (
                <ProviderCardButton key={card.key} card={card} selected={card.key === selected.key}
                  blocked={card.connectors.reduce((sum, c) => sum + (blockedCount(c, allowedByConnector) ?? 0), 0)}
                  onSelect={() => selectCard(card)} />
              ))}
          </div>

          <div className="space-y-4">
            {selected.state !== "not_connected" ? selected.connectors.map((connector) => (
              <ConnectionPanel key={connector.id} title={selected.connectors.length > 1 ? connector.name : selected.title}
                connector={connector} blocked={blockedCount(connector, allowedByConnector)}
                allowed={allowedByConnector.get(connector.id)?.size ?? null}
                onReconnect={() => startOAuthM.mutate({ provider: connector.provider as StartMcpOAuthInput["provider"], name: connector.name })}
                reconnecting={startOAuthM.isPending}
                onRefresh={() => discoverConnectorM.mutate(connector.id)}
                refreshing={discoverConnectorM.isPending && discoverConnectorM.variables === connector.id}
                onDisconnect={() => deleteConnectorM.mutate(connector.id)}
                disconnecting={deleteConnectorM.isPending && deleteConnectorM.variables === connector.id}
                onToggleWrites={() => settingsM.mutate({ connectorId: connector.id, readOnly: !connector.read_only })}
                togglingWrites={settingsM.isPending && settingsM.variables?.connectorId === connector.id}
                toggleError={settingsM.isError && settingsM.variables?.connectorId === connector.id ? mutationErrorMessage(settingsM.error, "Could not change read-only.") : null} />
            )) : null}

            {selected.state !== "not_connected" && selected.preset && !addingAnother ? (
              <button type="button" className="text-sm font-medium text-primary" onClick={() => setAddingAnother(true)}>
                Connect another {selected.title} account
              </button>
            ) : null}

            {showConnectForm && selected.preset ? (
              <form onSubmit={saveConnector} className="rounded-lg border border-border bg-card p-4">
                <div className="mb-4 flex items-center justify-between gap-2">
                  <h3 className="text-lg font-semibold">Connect {formPreset.title}</h3>
                  <StatePill state="not_connected" />
                </div>
                <div className="space-y-3">
                  <Field label="Connection name">
                    <Input value={connectorName} onChange={(e) => setConnectorName(e.target.value)} placeholder={`${formPreset.title} production`} />
                  </Field>
                  {authType === "oauth" ? (
                    <p className="text-sm leading-6 text-muted-foreground">
                      You'll sign in to {formPreset.title} and approve access. Your token is stored on the MCP gateway, never in the browser.
                    </p>
                  ) : (
                    <>
                      {!isCatalogPreset && (
                        <Field label="Base URL">
                          <Input value={connectorUrl} onChange={(e) => setConnectorUrl(e.target.value)} placeholder={formPreset.base_url_placeholder} />
                        </Field>
                      )}
                      <Field label="Credential name">
                        <Input value={credentialLabel} onChange={(e) => setCredentialLabel(e.target.value)} placeholder={formPreset.credential_label} />
                      </Field>
                      <Field label="Credential">
                        <div className="relative">
                          <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input type="password" value={credentialSecret} onChange={(e) => setCredentialSecret(e.target.value)} placeholder="Paste token, API key, or gateway secret" className="pl-9" />
                        </div>
                      </Field>
                    </>
                  )}
                  {startOAuthM.isError ? (
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                      {mutationErrorMessage(startOAuthM.error, `Could not start ${formPreset.title} sign-in from the MCP gateway.`)}
                    </div>
                  ) : null}
                  {createConnectorM.isError ? (
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                      {mutationErrorMessage(createConnectorM.error, "Could not save the connection in the MCP gateway.")}
                    </div>
                  ) : null}
                  <button type="submit" className="studio-button studio-button-primary w-full"
                    disabled={!connectorName.trim() || (authType === "manual" && !isCatalogPreset && !connectorUrl.trim()) || (authType === "manual" && isCatalogPreset && !credentialSecret.trim()) || createConnectorM.isPending || startOAuthM.isPending}>
                    {authType === "oauth" ? <PlugZap className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    {authType === "oauth"
                      ? startOAuthM.isPending ? "Opening sign-in..." : `Continue with ${formPreset.title}`
                      : createConnectorM.isPending ? "Connecting..." : `Connect ${formPreset.title}`}
                  </button>
                  <p className="text-xs leading-5 text-muted-foreground">
                    After you connect, agents can use {formPreset.title}'s tools. You can make the connection read-only at any time.
                  </p>
                  {addingAnother ? (
                    <button type="button" className="text-sm text-muted-foreground hover:text-foreground" onClick={() => setAddingAnother(false)}>Cancel</button>
                  ) : null}
                </div>
              </form>
            ) : null}
          </div>
        </div>
      </div>

      <ToolGroups tools={liveTools} connectors={connectors} allowedByConnector={allowedByConnector}
        isFetching={mcpToolsQ.isFetching} isFetched={mcpToolsQ.isFetched} isError={mcpToolsQ.isError}
        onRefresh={() => void mcpToolsQ.refetch()} />
    </div>
  );
}

function StatePill({ state }: { state: ProviderCard["state"] }) {
  if (state === "connected")
    return <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2.5 py-0.5 text-xs font-semibold text-success"><span className="h-1.5 w-1.5 rounded-full bg-success" />Connected</span>;
  if (state === "needs_reconnect")
    return <span className="rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-semibold text-destructive">Needs reconnect</span>;
  return <span className="rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground">Not connected</span>;
}

function ReadOnlyPill() {
  return <span className="rounded-full bg-warning/10 px-2.5 py-0.5 text-xs font-semibold text-warning">Read-only</span>;
}

function ProviderCardButton({ card, selected, blocked, onSelect }: { card: ProviderCard; selected: boolean; blocked: number; onSelect: () => void }) {
  const footer = card.state === "not_connected"
    ? <span className="text-primary">Connect →</span>
    : card.state === "needs_reconnect"
      ? <span className="text-destructive">Sign-in expired. Reconnect to use its tools.</span>
      : <span>
          {card.toolCount} tool{card.toolCount === 1 ? "" : "s"}
          {card.readOnly && blocked ? ` · ${blocked} blocked by read-only` : ""}
          {card.connectors.length > 1 ? ` · ${card.connectors.length} connections` : ""}
        </span>;
  return (
    <button type="button" aria-pressed={selected} onClick={onSelect}
      className={cn("flex min-h-36 flex-col gap-2 rounded-lg border p-4 text-left transition-colors",
        selected ? "border-primary bg-primary/5" : card.state === "needs_reconnect" ? "border-destructive/40 bg-card hover:bg-muted/60" : "border-border bg-card hover:bg-muted/60")}>
      <span className="flex items-start justify-between gap-3">
        <span className="inline-flex items-center gap-2 font-semibold"><PlugZap className="h-4 w-4 text-primary" />{card.title}</span>
        <span className="flex flex-wrap justify-end gap-1.5"><StatePill state={card.state} />{card.readOnly ? <ReadOnlyPill /> : null}</span>
      </span>
      {card.description ? <span className="text-sm leading-6 text-muted-foreground">{card.description}</span> : null}
      <span className="mt-auto text-sm text-muted-foreground">{footer}</span>
    </button>
  );
}

type ConnectionPanelProps = {
  title: string;
  connector: McpConnector;
  blocked: number | null;
  allowed: number | null;
  onReconnect: () => void;
  reconnecting: boolean;
  onRefresh: () => void;
  refreshing: boolean;
  onDisconnect: () => void;
  disconnecting: boolean;
  onToggleWrites: () => void;
  togglingWrites: boolean;
  toggleError: string | null;
};

function ConnectionPanel({ title, connector, blocked, allowed, onReconnect, reconnecting, onRefresh, refreshing, onDisconnect, disconnecting, onToggleWrites, togglingWrites, toggleError }: ConnectionPanelProps) {
  const usable = isConnectorUsable(connector.status);
  const refreshed = formatRefreshed(connector.last_discovered_at);
  const switchId = `allow-writes-${connector.id}`;
  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-lg font-semibold">{title}</h3>
          <StatePill state={usable ? "connected" : "needs_reconnect"} />
        </div>
        <div className="truncate font-mono text-xs text-muted-foreground">{connector.base_url}</div>
      </div>

      <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
        <dt className="text-muted-foreground">Signed in</dt>
        <dd>{connector.auth_type === "oauth" ? "OAuth, with your account" : `${connector.credential_label}: ${connector.secret_preview}`}</dd>
        <dt className="text-muted-foreground">Tools</dt>
        <dd>{connector.tool_count} found{refreshed ? ` · refreshed ${refreshed}` : ""}</dd>
        {connector.provider.startsWith("custom-") ? (<><dt className="text-muted-foreground">Added by</dt><dd>Your org admin</dd></>) : null}
      </dl>

      {connector.last_error ? <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">{humanMcpError(connector.last_error)}</div> : null}

      <div className={cn("space-y-2 rounded-md border p-3", connector.read_only ? "border-warning/40 bg-warning/5" : "border-border bg-muted/20")}>
        <div className="flex items-center justify-between gap-3">
          <span className={cn("inline-flex items-center gap-2 text-sm font-semibold", connector.read_only && "text-warning")}>
            {connector.read_only ? <Lock className="h-4 w-4" /> : null}
            {connector.read_only ? "Read-only" : "Writes allowed"}
          </span>
          <label htmlFor={switchId} className="flex cursor-pointer items-center gap-2 text-sm">
            Allow writes
            <input id={switchId} type="checkbox" role="switch" className="h-4 w-8 cursor-pointer accent-primary"
              checked={!connector.read_only} disabled={togglingWrites} onChange={onToggleWrites} />
          </label>
        </div>
        <p className="text-xs leading-5 text-muted-foreground">
          {connector.read_only
            ? allowed !== null && blocked !== null
              ? `Agents can use ${allowed} read tool${allowed === 1 ? "" : "s"} (get, list, search). ${blocked} tool${blocked === 1 ? "" : "s"} that create, change or delete ${blocked === 1 ? "is" : "are"} blocked.`
              : "Agents can only use read tools (get, list, search). Tools that create, change or delete are blocked."
            : "Agents can create, change and delete with this connection. Turn off to make it read-only."}
        </p>
        {toggleError ? <p className="text-xs text-destructive">{toggleError}</p> : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {connector.auth_type === "oauth" && !usable ? (
          <button type="button" onClick={onReconnect} className="studio-button studio-button-primary" disabled={reconnecting}>
            <PlugZap className="h-4 w-4" /> Reconnect
          </button>
        ) : connector.auth_type !== "oauth" && connector.status === "expired" ? (
          <span className="self-center text-xs text-muted-foreground">Remove it and connect again with a new token.</span>
        ) : null}
        <button type="button" onClick={onRefresh} className="studio-button" disabled={refreshing}>
          <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} /> Refresh tools
        </button>
        <button type="button" onClick={onDisconnect} className="studio-button text-destructive" disabled={disconnecting}>
          <Trash2 className="h-4 w-4" /> Disconnect
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold uppercase text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
